import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, ClipboardCheck, Bus, Compass, Mail } from "lucide-react";
import { IMAGES, BRAND } from "@/site";
import { Section, SectionTitle, FeatureCard, BulletGrid, CTABand } from "@/components/ui-blocks/Blocks";

const WHY = [
  "Safety-first training approach",
  "Real-world school bus driving experience",
  "Professional trainer leadership",
  "Structured pre-trip inspection instruction",
  "Corrective and remedial driver coaching",
  "Refresher training support",
  "Plain-language training materials",
  "Future digital training tools",
  "District support services",
  "Workforce development solutions",
  "Practical instruction based on real school bus operations",
];

const OFFER = [
  { icon: Bus, title: "Driver Training Support", body: "New and developing drivers prepared for the responsibilities of school bus operation." },
  { icon: ClipboardCheck, title: "Pre-Trip Inspection", body: "Detailed inside / outside inspection coaching with practical, plain-language explanations." },
  { icon: ShieldCheck, title: "Refresher & Corrective", body: "Targeted retraining to rebuild confidence and correct unsafe habits." },
  { icon: Compass, title: "District Support", body: "Workforce development, evaluations and training resources for transportation departments." },
];

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section data-testid="home-hero" className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.heroHome} alt="Yellow school bus on the road" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09090b]/70 via-[#09090b]/85 to-[#09090b]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-24 pb-24 md:pt-36 md:pb-40">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-2 w-2 bg-[#FFCC00] animate-pulse" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#FFCC00] font-semibold">
              Delaware · Preparing for launch
            </span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-bold uppercase tracking-tight leading-[0.9] max-w-5xl">
            Building safer<br />
            <span className="text-[#FFCC00]">school bus</span> drivers.
          </h1>
          <p className="mt-6 text-[11px] md:text-xs uppercase tracking-[0.4em] text-white/70 font-mono">
            Coach · Learn · Build
          </p>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-white/75 leading-relaxed">
            CLB Solution LLC provides professional school bus driver training support, pre-trip
            inspection coaching, refresher training, corrective safety instruction, and digital
            learning tools designed to help prepare safe, confident, and professional drivers.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="hero-cta-request-info"
              className="inline-flex items-center gap-2 bg-[#FFCC00] text-black font-bold uppercase tracking-[0.18em] text-xs px-7 py-4 hover:bg-[#E6B800] transition-colors"
            >
              Request Information <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-cta-contact"
              className="inline-flex items-center gap-2 border-2 border-white/20 text-white font-bold uppercase tracking-[0.18em] text-xs px-7 py-4 hover:border-white transition-colors"
            >
              Contact CLB Solution
            </Link>
            <Link
              to="/training-programs"
              data-testid="hero-cta-programs"
              className="inline-flex items-center gap-2 border-2 border-white/20 text-white font-bold uppercase tracking-[0.18em] text-xs px-7 py-4 hover:border-white transition-colors"
            >
              Explore Training Programs
            </Link>
          </div>
        </div>

        {/* hazard stripe footer */}
        <div className="hazard-stripes h-3 relative" />
      </section>

      {/* MARQUEE */}
      <div className="border-y border-white/10 overflow-hidden bg-[#0c0c0e]" data-testid="home-marquee">
        <div className="flex marquee-track whitespace-nowrap py-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-10 px-6 text-[11px] uppercase tracking-[0.35em] font-mono text-white/40">
              <span>Safety First</span><span className="text-[#FFCC00]">●</span>
              <span>Pre-Trip Mastery</span><span className="text-[#FFCC00]">●</span>
              <span>Mirror Awareness</span><span className="text-[#FFCC00]">●</span>
              <span>Danger Zone Discipline</span><span className="text-[#FFCC00]">●</span>
              <span>Loading & Unloading</span><span className="text-[#FFCC00]">●</span>
              <span>Railroad Crossings</span><span className="text-[#FFCC00]">●</span>
              <span>Defensive Driving</span><span className="text-[#FFCC00]">●</span>
              <span>Refresher Training</span><span className="text-[#FFCC00]">●</span>
              <span>Corrective Coaching</span><span className="text-[#FFCC00]">●</span>
            </div>
          ))}
        </div>
      </div>

      {/* WELCOME */}
      <Section testid="home-welcome">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionTitle eyebrow="Welcome" title="More than operating a vehicle." />
          </div>
          <div className="lg:col-span-7 space-y-5 text-white/75 leading-relaxed text-base md:text-lg">
            <p>
              School bus driving is a responsibility that requires discipline, awareness, patience,
              professionalism, and a strong commitment to safety.
            </p>
            <p>
              At CLB Solution LLC, we focus on developing drivers through structured coaching,
              practical training, and real-world safety education. Our goal is to help build drivers
              who understand not only how to operate a school bus, but how to protect students,
              communicate professionally, follow proper procedures, and make safe decisions under
              pressure.
            </p>
            <p className="text-white/55">
              Whether you are a new trainee, returning driver, transportation department, or
              contractor, CLB Solution is being built to support stronger training and safer
              transportation.
            </p>
          </div>
        </div>
      </Section>

      {/* WHAT WE OFFER */}
      <Section className="bg-[#0c0c0e] border-y border-white/10" testid="home-offer">
        <SectionTitle eyebrow="What We Offer" title="Training support across the full driver journey." />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {OFFER.map((o, i) => (
            <div key={o.title} className="bg-[#09090b] p-8 hover:bg-[#18181b] transition-colors group">
              <div className="font-mono text-[10px] text-[#FFCC00] mb-5">{String(i + 1).padStart(2, "0")} /</div>
              <o.icon size={28} className="text-[#FFCC00] mb-5" strokeWidth={1.5} />
              <h3 className="font-display text-xl font-bold uppercase tracking-tight mb-3">{o.title}</h3>
              <p className="text-sm text-white/65 leading-relaxed">{o.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* WHY CHOOSE */}
      <Section testid="home-why">
        <SectionTitle eyebrow="Why Choose CLB Solution" title="Built on real-world experience." description="Safety-focused coaching, patient instruction, and practical materials from a transportation professional with nearly two decades behind the wheel." />
        <BulletGrid items={WHY} testid="home-why-grid" />
      </Section>

      {/* COMING SOON APP */}
      <Section className="bg-[#0c0c0e] border-y border-white/10" testid="home-app-teaser">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFCC00] mb-3 font-semibold">Coming Soon</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight leading-[1]">
              CLB Solution<br />Pretrip Guide
            </h2>
            <p className="mt-5 text-white/70 max-w-xl leading-relaxed">
              A mobile-friendly training app in development to help trainees study and review the
              full inside and outside pre-trip inspection from any phone.
            </p>
            <div className="mt-7">
              <Link to="/pretrip-guide" data-testid="home-app-cta" className="inline-flex items-center gap-2 border-2 border-[#FFCC00] text-[#FFCC00] font-bold uppercase tracking-[0.18em] text-xs px-6 py-4 hover:bg-[#FFCC00] hover:text-black transition-colors">
                See Planned Features <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative border border-white/10 bg-[#09090b] p-8 grid grid-cols-2 gap-px">
              {["Step-by-step pre-trip", "Inside & outside sections", "Brake check procedures", "Emergency equipment", "Visual learning", "Practice checklists", "Common mistakes", "Trainer tools"].map((t) => (
                <div key={t} className="bg-[#0c0c0e] p-4 text-xs uppercase tracking-wider text-white/75 border border-white/5">{t}</div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <CTABand />
    </>
  );
}
